import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { StatusTabs } from 'components/ui/status-tabs';
import { toPlainText } from 'modules/editor/utils';
import { QUESTION_REPORT_STATUSES, type QuestionReportReason, type QuestionReportStatus } from '../constants';
import type { QuestionReportInbox, QuestionReportInboxItem } from '../types';

export interface QuestionReportsInboxViewProps {
  inbox: QuestionReportInbox;
  status: QuestionReportStatus;
  onStatusChange: (status: QuestionReportStatus) => void;
  /** Holds the edit permission: without it the list is read-only. The API enforces this too. */
  canTriage: boolean;
  /** The test's questions tab, where the author fixes the question. */
  questionsHref: string;
  /** The question whose status is being updated right now, so its buttons wait. */
  pendingQuestionId: string | undefined;
  onSetStatus: (questionId: string, status: QuestionReportStatus) => void;
}

/** The reported questions of one test, most reported first. Nobody's identity is shown: the API does not send it. */
export function QuestionReportsInboxView({
  inbox,
  status,
  onStatusChange,
  canTriage,
  questionsHref,
  pendingQuestionId,
  onSetStatus,
}: QuestionReportsInboxViewProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <StatusTabs
        aria-label={t('question_reports.status_label')}
        value={status}
        onChange={onStatusChange}
        options={QUESTION_REPORT_STATUSES.map((value) => ({
          value,
          label: t(`question_reports.status_${value}`),
          count: inbox.totals[value],
        }))}
      />
      {inbox.items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
          {t(`question_reports.empty_${status}`)}
        </p>
      ) : (
        <ul className="space-y-3">
          {inbox.items.map((item) => (
            <InboxRow
              key={item.questionId}
              item={item}
              status={status}
              canTriage={canTriage}
              questionsHref={questionsHref}
              pending={pendingQuestionId === item.questionId}
              onSetStatus={onSetStatus}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

interface InboxRowProps {
  item: QuestionReportInboxItem;
  status: QuestionReportStatus;
  canTriage: boolean;
  questionsHref: string;
  pending: boolean;
  onSetStatus: (questionId: string, status: QuestionReportStatus) => void;
}

function InboxRow({ item, status, canTriage, questionsHref, pending, onSetStatus }: InboxRowProps) {
  const { t } = useTranslation();
  const prompt = toPlainText(item.prompt);
  const reasons = Object.entries(item.reasons) as Array<[QuestionReportReason, number]>;

  return (
    <li className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          {item.order !== undefined && (
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('question_reports.question_number', { number: item.order })}
            </p>
          )}
          <p className="mt-0.5 line-clamp-3 text-sm font-semibold text-foreground">
            {prompt || t('question_reports.deleted_question')}
          </p>
        </div>
        <p className="shrink-0 text-sm tabular-nums text-muted-foreground">
          {t('question_reports.report_count', { count: item.count })}
        </p>
      </div>

      <ul className="mt-3 flex flex-wrap gap-2" aria-label={t('question_reports.reasons_label')}>
        {reasons.map(([reason, count]) => (
          <li key={reason} className="rounded-full border border-border px-2.5 py-1 text-xs font-medium text-foreground">
            {t(`question_reports.reason_${reason}`)} · <span className="tabular-nums">{count}</span>
          </li>
        ))}
      </ul>

      {item.comments.length > 0 && (
        <ul className="mt-3 space-y-2 border-t border-border pt-3">
          {item.comments.map((entry, index) => (
            <li key={`${entry.at}-${index}`} className="text-sm text-foreground">
              <span className="text-xs text-muted-foreground">{t(`question_reports.reason_${entry.reason}`)}: </span>
              {/* Plain text only: it is whatever a learner typed. */}
              {entry.comment}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <Link
          to={questionsHref}
          className="mr-auto text-sm font-medium text-foreground underline underline-offset-2 hover:no-underline"
        >
          {t('question_reports.open_questions')}
        </Link>
        {canTriage &&
          (status === 'OPEN' ? (
            <>
              <Button type="button" variant="outline" disabled={pending} onClick={() => onSetStatus(item.questionId, 'DISMISSED')}>
                {t('question_reports.dismiss')}
              </Button>
              <Button type="button" disabled={pending} onClick={() => onSetStatus(item.questionId, 'RESOLVED')}>
                {t('question_reports.resolve')}
              </Button>
            </>
          ) : (
            <Button type="button" variant="outline" disabled={pending} onClick={() => onSetStatus(item.questionId, 'OPEN')}>
              {t('question_reports.reopen')}
            </Button>
          ))}
      </div>
    </li>
  );
}
